import type { Meta, StoryObj } from '@storybook/angular';
import { moduleMetadata } from '@storybook/angular';
import { expect, within } from 'storybook/test';
import { ProductWidgetComponent } from '../../app/content/layout/components/product/product-widget/product-widget.component';
import { withCommonDecorators } from '../_utils/story-helpers';
import {
  createDamilanoBarberaProduct,
  createDiscountedProduct,
  createQuantityPricedProduct,
  createGroupVoucherProduct,
  createProductWithRatings,
  createLastBottlesProduct,
  createDiscountCountdownProduct,
} from '../_mocks/wine-product.mock';

const meta: Meta = {
  title: 'Compositions/Product Grid',
  tags: ['autodocs'],
  decorators: [
    ...withCommonDecorators(),
    moduleMetadata({ imports: [ProductWidgetComponent] }),
  ],
  parameters: {
    layout: 'padded',
    docs: {
      description: {
        component: 'Product widget grid compositions showing how multiple product cards render side-by-side in different layouts and states.',
      },
    },
  },
};
export default meta;
type Story = StoryObj;

const regularProduct = createDamilanoBarberaProduct();
const discountedProduct = createDiscountedProduct();
const quantityProduct = createQuantityPricedProduct();
const voucherProduct = createGroupVoucherProduct();
const ratedProduct = createProductWithRatings();
const outOfStockProduct = createDamilanoBarberaProduct({ id: '1006', title: 'Damilano Barbera 2024 \u2013 Udsolgt', inStock: false, stock: 0 });
const lastBottlesProduct = createLastBottlesProduct();
const discountCountdownProduct = createDiscountCountdownProduct();

export const ThreeColumnGrid: Story = {
  render: () => ({
    template: `
      <div class="row g-3">
        <div class="col-4"><article app-product-widget [product]="regular" [index]="0" [eventSourceName]="'storybook'"></article></div>
        <div class="col-4"><article app-product-widget [product]="discounted" [index]="1" [eventSourceName]="'storybook'"></article></div>
        <div class="col-4"><article app-product-widget [product]="quantity" [index]="2" [eventSourceName]="'storybook'"></article></div>
      </div>
    `,
    props: {
      regular: regularProduct,
      discounted: discountedProduct,
      quantity: quantityProduct,
    },
  }),
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/damilano barbera/i)).toBeInTheDocument();
    await expect(canvasElement.querySelectorAll('[app-product-widget]').length).toBe(3);
  },
};

export const TwoColumnGrid: Story = {
  render: () => ({
    template: `
      <div class="row g-3">
        <div class="col-6"><article app-product-widget [product]="regular" [index]="0" [eventSourceName]="'storybook'"></article></div>
        <div class="col-6"><article app-product-widget [product]="rated" [index]="1" [eventSourceName]="'storybook'"></article></div>
      </div>
    `,
    props: {
      regular: regularProduct,
      rated: ratedProduct,
    },
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelectorAll('[app-product-widget]').length).toBe(2);
  },
};

export const AllProductStates: Story = {
  render: () => ({
    template: `
      <h3 class="mb-3" style="font-family: serif;">All Product States</h3>
      <div class="row g-3">
        <div class="col-12 col-md-4"><article app-product-widget [product]="regular" [index]="0" [eventSourceName]="'storybook'"></article></div>
        <div class="col-12 col-md-4"><article app-product-widget [product]="discounted" [index]="1" [eventSourceName]="'storybook'"></article></div>
        <div class="col-12 col-md-4"><article app-product-widget [product]="quantity" [index]="2" [eventSourceName]="'storybook'"></article></div>
        <div class="col-12 col-md-4"><article app-product-widget [product]="voucher" [index]="3" [eventSourceName]="'storybook'"></article></div>
        <div class="col-12 col-md-4"><article app-product-widget [product]="rated" [index]="4" [eventSourceName]="'storybook'"></article></div>
        <div class="col-12 col-md-4"><article app-product-widget [product]="outOfStock" [index]="5" [eventSourceName]="'storybook'"></article></div>
        <div class="col-12 col-md-4"><article app-product-widget [product]="lastBottles" [index]="6" [eventSourceName]="'storybook'"></article></div>
        <div class="col-12 col-md-4"><article app-product-widget [product]="discountCountdown" [index]="7" [eventSourceName]="'storybook'"></article></div>
      </div>
    `,
    props: {
      regular: regularProduct,
      discounted: discountedProduct,
      quantity: quantityProduct,
      voucher: voucherProduct,
      rated: ratedProduct,
      outOfStock: outOfStockProduct,
      lastBottles: lastBottlesProduct,
      discountCountdown: discountCountdownProduct,
    },
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelectorAll('[app-product-widget]').length).toBe(8);
  },
};

export const SingleColumn: Story = {
  render: () => ({
    template: `
      <div class="row g-3">
        <div class="col-12" style="max-width: 350px;">
          <article app-product-widget [product]="regular" [index]="0" [eventSourceName]="'storybook'"></article>
        </div>
      </div>
    `,
    props: { regular: regularProduct },
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelectorAll('[app-product-widget]').length).toBe(1);
  },
};
